import { Box, ButtonBase, Menu, MenuItem } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";

import type { SupportAssignableUser } from "../../types.js";

/**
 * Click-to-change assignee pill. Mirrors the visual language of
 * `<TransitionMenu>` (slim pill, chevron, dropdown).
 *
 * Renders just the assignee's name — the row label "Assignee" already
 * lives on the parent DetailRow, so prefixing with "Assignee:" again
 * inside the pill was redundant.
 */
export function AssignMenu({
  current,
  candidates,
  onAssign,
  isPending,
}: {
  current: { id: number; name: string } | null;
  candidates: SupportAssignableUser[];
  onAssign: (assigneeId: number | null) => void;
  isPending: boolean;
}): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <Box sx={{ display: "inline-flex" }}>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={isPending}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Change assignee"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          borderRadius: 999,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.default",
          px: 1.5,
          py: 0.5,
          fontSize: 12,
          fontWeight: 500,
          color: "text.primary",
          "&:hover": { bgcolor: "surfaceSoft" },
          "&.Mui-disabled": { opacity: 0.5 },
        }}
      >
        {current ? (
          <>
            <Initials name={current.name} />
            <span>{current.name}</span>
          </>
        ) : (
          <Box component="span" sx={{ fontStyle: "italic", color: "text.disabled" }}>Unassigned</Box>
        )}
        <Box component="svg" viewBox="0 0 8 8" aria-hidden sx={{ height: 8, width: 8, opacity: 0.7 }}>
          <path d="M1 3l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </Box>
      </ButtonBase>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ role: "menu", dense: true, sx: { maxHeight: 288, width: 256 } }}
      >
        <MenuItem
          role="menuitem"
          onClick={() => {
            setAnchorEl(null);
            if (current !== null) onAssign(null);
          }}
          sx={{ gap: 1, fontSize: 12, fontStyle: "italic", color: "text.disabled" }}
        >
          Unassign
        </MenuItem>
        {candidates.map((u) => {
          const fullName =
            [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email;
          const isCurrent = current?.id === u.id;
          return (
            <MenuItem
              key={u.id}
              role="menuitem"
              onClick={() => {
                setAnchorEl(null);
                if (!isCurrent) onAssign(u.id);
              }}
              sx={[
                { gap: 1, fontSize: 12 },
                isCurrent
                  ? {
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                      color: "primary.main",
                    }
                  : { color: "text.primary" },
              ]}
            >
              <Initials name={fullName} />
              <Box component="span" sx={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {fullName}
              </Box>
              <Box
                component="span"
                sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
              >
                {u.role}
              </Box>
            </MenuItem>
          );
        })}
        {candidates.length === 0 && (
          <MenuItem disabled sx={{ fontSize: 12, color: "text.disabled" }}>
            No assignable staff.
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

/** Tiny avatar tile — initials from the name, brand-tinted background. */
function Initials({ name }: { name: string }): JSX.Element {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join("");
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: "inline-flex",
        height: 16,
        width: 16,
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        fontSize: 9,
        fontWeight: 600,
        color: "primary.main",
      }}
    >
      {initials || "?"}
    </Box>
  );
}
